import {
  configureStore,
  isFulfilled,
  isRejected,
  Middleware,
  MiddlewareAPI,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { accountApi } from 'services/AccountService';
import { roomApi } from 'services/RoomService';
import { statusApi } from 'services/StatusService';
import { thingsApi } from 'services/ThingsService';
import { unitsApi } from 'services/UnitsService';
import { signInApi, usersApi } from 'services/UsersService';
import networkReducer, { fulfilledRequest, increaseFetchErrorCount } from 'slices/NetworkSlice';
import signUpReducer from './slices/SignUpSlice';

const RTKQueryTypes = ['query', 'mutation'];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isRTKQueryAction = (action: any) => RTKQueryTypes.includes(action?.meta?.arg?.type);

export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  if (
    isRejected(action) &&
    isRTKQueryAction(action) &&
    action?.error?.name === 'AbortError' &&
    action?.error?.message === 'Aborted'
  ) {
    // RTQ Query async action which failed due to timeout
    // TIMEOUT_ERROR is not dispatched (https://github.com/reduxjs/redux-toolkit/issues/2915)
    api.dispatch(increaseFetchErrorCount());
  } else if (isFulfilled(action) && isRTKQueryAction(action)) {
    api.dispatch(fulfilledRequest());
  }

  return next(action);
};

export const store = configureStore({
  reducer: {
    ['signUp']: signUpReducer,
    ['network']: networkReducer,
    [thingsApi.reducerPath]: thingsApi.reducer,
    [roomApi.reducerPath]: roomApi.reducer,
    [signInApi.reducerPath]: signInApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [accountApi.reducerPath]: accountApi.reducer,
    [statusApi.reducerPath]: statusApi.reducer,
    [unitsApi.reducerPath]: unitsApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      rtkQueryErrorLogger,
      thingsApi.middleware,
      roomApi.middleware,
      signInApi.middleware,
      usersApi.middleware,
      accountApi.middleware,
      statusApi.middleware,
      unitsApi.middleware,
    ),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch: () => AppDispatch = useDispatch;
